import { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, Button, Typography } from '@mui/material'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { GoBack } from '../../../../components/goBack'
import StepperCreate from '../../../../components/stepperCreate'
import Loader from '../../../../components/loader'
import SimpleModal from '../../../../components/modal/simpleModal'
import SnackSimple from '../../../../components/snackbars/simple'
import { stepsArray } from './constants'
import { actionPlansServices } from '../../../../service/actionPlans'
import { httpFetch } from '../../../../utils/crud'
import { getPlanToGenerateById, getAvailableUsersForActionPlan, getStoresForFilter, buildCreateActionPlanPayload } from './methods'
import { PlanToGenerateInfo, CreateActionPlanData, StoreFilterOption, UserFilters, AvailableUser } from './types'
import { useStyles } from './styles'
import { useContextState } from '../../../../hooks/theme'
import { Types } from '../../../../utils/enums'
import { isEmail } from '../../../../utils/validate/validate'

// Step fragments
import Step1UserWithStore from './fragments/step1UserWithStore'
import Step1UserWithoutStore from './fragments/step1UserWithoutStore'
import Step1Store from './fragments/step1Store'
import Step2Questions from './fragments/step2Questions'
import Step3Evidences from './fragments/step3Evidences'
import Step4Notifications from './fragments/step4Notifications'

const CreateActionPlan = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const styles = useStyles()
  const { colors } = useContextState()

  const [step, setStep] = useState(0)
  const [planInfo, setPlanInfo] = useState<PlanToGenerateInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [usersError, setUsersError] = useState(false)
  const [storeFilterOptions, setStoreFilterOptions] = useState<StoreFilterOption[]>([])
  const [isLoadingStores, setIsLoadingStores] = useState(false)
  const [allAvailableUsers, setAllAvailableUsers] = useState<AvailableUser[]>([])
  const [data, setData] = useState<CreateActionPlanData>({
    title: '',
    description: '',
    selectedUser: null,
    selectedQuestions: [],
    selectedEvidences: [],
    selectedQuestionId: null,
    selectedInternalUsers: [],
    externalEmails: [],
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    isLoading: false,
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'error' as Types,
  })

  const loadPlanInfo = useCallback(async () => {
    if (!id) return
    setIsLoading(true)
    setLoadError(false)
    try {
      const info = await getPlanToGenerateById(parseInt(id))
      if (info) {
        setPlanInfo(info)
      } else {
        setLoadError(true)
      }
    } catch (error) {
      console.error('Error loading plan info:', error)
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadPlanInfo()
  }, [loadPlanInfo])

  const loadAvailableUsers = useCallback(async (filters?: UserFilters) => {
    setIsLoadingUsers(true)
    setUsersError(false)
    try {
      const users = await getAvailableUsersForActionPlan(filters)
      setPlanInfo((prev) => (prev ? { ...prev, availableUsers: users } : prev))
      if (!filters) {
        setAllAvailableUsers(users)
      }
    } catch (error) {
      console.error('Error loading available users:', error)
      setUsersError(true)
    } finally {
      setIsLoadingUsers(false)
    }
  }, [])

  const loadStoreFilterOptions = useCallback(async () => {
    setIsLoadingStores(true)
    try {
      const stores = await getStoresForFilter()
      setStoreFilterOptions(stores)
    } catch (error) {
      console.error('Error loading stores for filter:', error)
    } finally {
      setIsLoadingStores(false)
    }
  }, [])

  useEffect(() => {
    if (planInfo?.mode === 'USER_WITHOUT_STORE') {
      loadAvailableUsers()
      loadStoreFilterOptions()
    }
  }, [planInfo?.mode, loadAvailableUsers, loadStoreFilterOptions])

  const handleChange = useCallback((newData: Partial<CreateActionPlanData>) => {
    setData((prev) => ({ ...prev, ...newData }))
    // Clear error when user types or selects
    if (newData.title !== undefined) {
      setErrors((prev) => ({ ...prev, title: '' }))
    }
    if (newData.selectedUser !== undefined) {
      setErrors((prev) => ({ ...prev, selectedUser: '' }))
    }
    if (newData.selectedQuestions !== undefined) {
      setErrors((prev) => ({ ...prev, selectedQuestions: '' }))
    }
    if (newData.selectedEvidences !== undefined) {
      setErrors((prev) => ({ ...prev, selectedEvidences: '' }))
    }
    if (newData.externalEmails !== undefined) {
      setErrors((prev) => ({ ...prev, externalEmails: '' }))
    }
  }, [])

  const validateStep = useCallback((): boolean => {
    const newErrors: Record<string, string> = {}
    let isValid = true

    if (step === 0) {
      if (!data.title.trim()) {
        newErrors.title = t('general.validations.required')
        isValid = false
      }

      if (planInfo?.mode === 'USER_WITHOUT_STORE' && data.selectedUser === null) {
        newErrors.selectedUser = t('actionPlan.validation.selectAtLeastOneUser')
        isValid = false
      }
    }

    if (step === 1 && data.selectedQuestions.length === 0) {
      newErrors.selectedQuestions = t('actionPlan.validation.selectAtLeastOneQuestion')
      isValid = false
    }

    if (step === 3 && data.externalEmails.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const invalidEmails = data.externalEmails.filter((e) => !emailRegex.test(e))
      if (invalidEmails.length > 0) {
        newErrors.externalEmails = t('actionPlan.validation.invalidEmails')
        isValid = false
      }
    }

    setErrors(newErrors)
    return isValid
  }, [data, planInfo, step, t])

  const handleNext = useCallback(() => {
    if (!validateStep()) return
    setStep((prev) => Math.min(prev + 1, 3))
  }, [validateStep])

  const handleBack = useCallback(() => {
    setStep((prev) => Math.max(prev - 1, 0))
  }, [])

  const handleGoBack = useCallback(() => {
    navigate('/dashboard/action-plan/to-generate')
  }, [navigate])

  const handleCreateActionPlan = useCallback(() => {
    setConfirmModal({ open: true, isLoading: false })
  }, [])

  const handleConfirmCreate = useCallback(async () => {
    if (!planInfo) return

    setConfirmModal({ open: true, isLoading: true })

    try {
      const payload = buildCreateActionPlanPayload(planInfo, data)
      const response = await actionPlansServices(httpFetch).saveAutomaticTempItems(payload)

      if (response.errors || response.code !== '00') {
        setConfirmModal({ open: false, isLoading: false })
        setSnackbar({
          open: true,
          message: t('actionPlan.errorCreatingActionPlan'),
          type: 'error' as Types,
        })
        return
      }

      setConfirmModal({ open: false, isLoading: false })
      navigate('/dashboard/action-plan/created')
    } catch (error) {
      console.error('Error creating action plan:', error)
      setConfirmModal({ open: false, isLoading: false })
      setSnackbar({
        open: true,
        message: t('actionPlan.errorCreatingActionPlan'),
        type: 'error' as Types,
      })
    }
  }, [navigate, planInfo, data, t])

  const handleCloseModal = useCallback(() => {
    setConfirmModal({ open: false, isLoading: false })
  }, [])

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  const hasInvalidExternalEmails = data.externalEmails.some((e) => !isEmail(e))

  const isNextDisabled = 
    (step === 0 && (
      !data.title.trim() || 
      (planInfo?.mode === 'USER_WITHOUT_STORE' && data.selectedUser === null)
    )) ||
    (step === 1 && data.selectedQuestions.length === 0) ||
    (step === 3 && hasInvalidExternalEmails)

  const selectedUser = useMemo(() => {
    return planInfo?.availableUsers?.find((user) => user.id === data.selectedUser)
      ?? allAvailableUsers.find((user) => user.id === data.selectedUser)
  }, [planInfo?.availableUsers, allAvailableUsers, data.selectedUser])

  const renderStepHeader = () => {
    if (!planInfo) return null
    return (
      <Box sx={styles.stepHeader}>
        <Box sx={styles.stepHeaderLeft}>
          <Box sx={styles.stepHeaderIconWrapper}>
            <InsertDriveFileOutlinedIcon sx={styles.stepHeaderIcon} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={styles.headerInfoLabel}>{t('dashboard.data.questionnaire')}</Typography>
            <Typography sx={styles.stepHeaderQuestionnaireName}>
              {planInfo.questionnaireName}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.stepHeaderRight}>
          {planInfo.store && (
            <Box sx={styles.headerInfoItem}>
              <Typography sx={styles.headerInfoLabel}>{t('actionPlan.unit')}</Typography>
              <Typography sx={styles.headerInfoValue}>
                {planInfo.store.keyCode} - {planInfo.store.name}
              </Typography>
            </Box>
          )}
          {planInfo.store?.managerName && planInfo.mode !== 'STORE' && (
            <Box sx={styles.headerInfoItem}>
              <Typography sx={styles.headerInfoLabel}>{t('actionPlan.storeManager')}</Typography>
              <Typography sx={styles.headerInfoValue}>{planInfo.store.managerName}</Typography>
            </Box>
          )}
          {planInfo.mode === 'USER_WITHOUT_STORE' && selectedUser && (
            <Box sx={styles.headerInfoItem}>
              <Typography sx={styles.headerInfoLabel}>{selectedUser.userType}</Typography>
              <Typography sx={styles.headerInfoValue}>{selectedUser.fullName}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  const renderStep = () => {
    if (!planInfo) return null
    switch (step) {
      case 0:
        switch (planInfo.mode) {
          case 'USER_WITH_STORE':
            return (
              <Step1UserWithStore
                planInfo={planInfo}
                data={data}
                onChange={handleChange}
                errors={errors}
              />
            )
          case 'USER_WITHOUT_STORE':
            return (
              <Step1UserWithoutStore
                planInfo={planInfo}
                data={data}
                onChange={handleChange}
                errors={errors}
                storeFilterOptions={storeFilterOptions}
                allAvailableUsers={allAvailableUsers}
                onApplyFilters={loadAvailableUsers}
                isLoadingStores={isLoadingStores}
                isLoadingUsers={isLoadingUsers}
                usersError={usersError}
              />
            )
          case 'STORE':
            return (
              <Step1Store
                planInfo={planInfo}
                data={data}
                onChange={handleChange}
                errors={errors}
              />
            )
          default:
            return null
        }
      case 1:
        return (
          <Step2Questions
            planInfo={planInfo}
            data={data}
            onChange={handleChange}
          />
        )
      case 2:
        return (
          <Step3Evidences
            planInfo={planInfo}
            data={data}
            onChange={handleChange}
          />
        )
      case 3:
        return (
          <Step4Notifications
            data={data}
            onChange={handleChange}
            errors={errors}
          />
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <Box sx={styles.container}>
        <Loader sx={{ height: 'calc(100% - 100px)' }} />
      </Box>
    )
  }

  if (loadError || !planInfo) {
    return (
      <Box sx={styles.container}>
        <Box sx={{ '&& .MuiTypography-root': { color: colors.color4 } }}>
          <GoBack path='/dashboard/action-plan/to-generate' title={t('general.returnToList')} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Typography color="error">{t('actionPlan.errorLoadingPlanInfo')}</Typography>
          <Button variant="contained" color="primary" onClick={loadPlanInfo}>
            {t('general.retry')}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box>
          <Box sx={{ '&& .MuiTypography-root': { color: colors.color4 } }}>
            <GoBack path='/dashboard/action-plan/to-generate' title={t('general.returnToList')} />
          </Box>
          <Typography variant='title' sx={styles.title}>
            {t('actionPlan.createActionPlan')}
          </Typography>
        </Box>
        <Box sx={{ width: '50%' }}>
          <StepperCreate steps={stepsArray} activeStep={step} />
        </Box>
      </Box>

      <Box sx={styles.stepWrapper}>
        {step !== 3 && renderStepHeader()}

        <Box sx={styles.stepContent}>
          {renderStep()}
        </Box>

        <Box sx={styles.stepFooter}>
          {step > 0 ? (
            <Button 
              variant='text' 
              onClick={handleBack} 
              sx={{ fontWeight: '500' }}
            >
              {t('general.return')}
            </Button>
          ) : (
            <Box />
          )}
          {step === 3 ? (
            <Button
              variant='contained'
              color='primary'
              onClick={handleCreateActionPlan}
              disabled={hasInvalidExternalEmails}
              sx={{ padding: '10px 28px', fontWeight: '500' }}
            >
              {t('actionPlan.createPlan')}
            </Button>
          ) : (
            <Button
              variant='contained'
              color='primary'
              onClick={handleNext}
              disabled={isNextDisabled}
              sx={{ padding: '10px 28px', fontWeight: '500' }}
            >
              {t('general.continue')}
            </Button>
          )}
        </Box>
      </Box>

      <SimpleModal
        open={confirmModal.open}
        close={handleCloseModal}
        title={t('actionPlan.createActionPlanConfirm')}
        description={t('actionPlan.createActionPlanConfirmDesc', {
          count: data.selectedQuestions.length,
          questionnaire: planInfo?.questionnaireName,
        })}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        next={handleConfirmCreate}
        back={handleCloseModal}
        loadingButton
        isLoading={confirmModal.isLoading}
        sx={styles.modalConfirmation}
        closeProps={{ sx: { position: 'absolute', right: '0px', top: '0px' } }} 
      />

      <SnackSimple
        open={snackbar.open}
        close={handleCloseSnackbar}
        message={snackbar.message}
        type={snackbar.type}
      />
    </Box>
  )
}

export default CreateActionPlan
